<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-sm) justify-center">
            <page-header> Features </page-header>
            <div class="max-w-(--breakpoint-sm) w-full flex flex-col gap-8">
                <kiosk-team-features-group
                    v-for="(group, index) in groups"
                    :key="group.type"
                    v-model="groups[index]"
                    :teamId="props.teamId"
                >
                </kiosk-team-features-group>
            </div>
        </div>
    </kiosk-team-details>
</template>

<script setup>
import KioskTeamDetails from '../details/KioskTeamDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import { ref } from 'vue';
import { adminFeaturesClient } from '~/services';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import { useToasted } from '~/composables/useToasted';
import KioskTeamFeaturesGroup from './KioskTeamFeaturesGroup.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

const toasted = useToasted();
const groups = ref([]);

const getFeatures = async () => {
    try {
        const response = await adminFeaturesClient.getFeatures(props.teamId);
        groupFeatures(response.data.data);
    } catch (e) {
        if (
            !e?.response?.status ||
            e?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            e?.response?.status === HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(e?.response?.data?.message || 'Something went wrong.');
        }
    }
};

const groupFeatures = (features) => {
    const excludeFeatures = ['automations_disabled', 'campaigns_disabled'];

    // Exclude features from Emergency Tools page
    features = features.filter((feature) => !excludeFeatures.includes(feature.id));

    const groupsData = [
        { name: 'General Availability', type: 'ga', features: [] },
        { name: 'Beta', type: 'beta', features: [] },
        { name: 'Alpha', type: 'alpha', features: [] },
        { name: 'Internal', type: 'internal', features: [] },
    ];

    features.forEach((feature) => {
        const group = groupsData.find((g) => g.type === feature.type);
        if (group) {
            group.features.push(feature);
        } else {
            // Handle unknown types by adding new groups
            groupsData.push({
                // Replace - or _ with space and capitalize the first letter of each word
                name: feature.type.replace(/[-_]/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase()),
                type: feature.type,
                features: [feature],
            });
        }
    });

    // Sort features alphabetically by name (backend-provided authoritative names)
    // within each type group for consistent UI display
    groupsData.forEach((group) => {
        group.features.sort((a, b) => a.name.localeCompare(b.name));
    });

    groups.value = groupsData.filter(
        (group) => group.features.length || group.type === 'beta' || group.type === 'alpha'
    );
};

getFeatures();
</script>
